import { computed, action, observable } from 'mobx';
import * as constants from '../../constants/constants';
import { GroupConfigurations } from './Item/GroupConfigurations';
import { GroupSingle } from './Item/GroupSingle';
import { log, hasOwnProperty } from '../../common/utils/index';
import * as pubSub from '../../common/utils/pub-sub';
import {
  CART_TRANSFER_INIT,
  CART_TRANSFER_READY,
  CART_TRANSFER_OPEN_MODAL,
  PRODUCT_SELECTOR_INIT,
} from '../../common/constants/events';
import { sortByNumber } from '../../helpers/helpers';
import { GroupSubstitutions } from './Item/GroupSubstitution';

export class CartStore {
  rootStore = null;

  @observable allGroups = [];

  @observable hasRetailersFlag = false;

  @observable cartTransferResponseData = {};

  removeAll = () => this.rootStore.alterationsStore.whenRemoveAll()
    .then(this.forceRemoveAll);

  getProducts = () =>
    this.allGroups.reduce((result, group) => {
      group.products.forEach((product) => {
        result.push({
          pid: product.ID,
          qty: product.quantity,
          href: product.href,
          hrefEncoded: product.hrefEncoded,
          imgAlt: product.imgAlt,
          imgSrc: product.imgSrc,
          commDesc: product.commDesc,
          commRef: product.commRef,
        });
      });
      return result;
    }, []);

  openCartTransferModal = () =>
    pubSub.publish(CART_TRANSFER_OPEN_MODAL, {
      products: this.getProducts(),
      cartTransferResponseData: this.cartTransferResponseData,
    });
  addGroups = (groups = []) => {
    const groupsArr = [];
    let shouldExpandGroup = !this.rootStore.uiStore.isMobile;

    groups.reverse(); // used for default expand latest added group (latest always last in incoming list)
    groups.forEach((group) => {
      switch (group.type) {
        case constants.groupType.GROUP:
          groupsArr.push(
            this.createGroup({ item: group, groupType: group.source, shouldExpandGroup }),
          );
          shouldExpandGroup = false;
          break;
        default:
          groupsArr.push(new GroupSingle(group, this.rootStore));
      }
    });
    return groupsArr;
  };

  constructor(groupsArray, rootStore) {
    this.rootStore = rootStore;
    this.allGroups = this.addGroups(groupsArray);
    this.initProductSelector();
  }

  initProductSelector = () => {
    if (this.allGroups.some((group) => !!group.codification && !group.backURL)) {
      pubSub.publish(PRODUCT_SELECTOR_INIT, true);
    }
  };

  @computed
  get selectByGroupType() {
    const curType = constants.groupType[this.rootStore.uiStore.type];
    let allConfs = [];

    switch (curType) {
      case constants.groupType.GROUP:
        allConfs = this.allGroups.filter((group) => group.type === constants.groupType.GROUP);
        return sortByNumber(allConfs, 'creationDate', -1);

      case constants.groupType.SINGLE:
        return this.allGroups.filter((group) => group.type === curType);

      case constants.groupType.ALL:
        return this.sortAllByCreationDate;
      default:
        log.warn('Unexpected select type');
        return [];
    }
  }

  @computed
  get sortAllByCreationDate() {
    const resultArray = [];

    this.allGroups.forEach((group) => {
      switch (group.type) {
        case constants.groupType.GROUP:
          resultArray.push(group);
          break;
        case constants.groupType.SINGLE:
          resultArray.push(...group.products);
          break;
        default:
          log.warn('[cartStore] Unexpected group type');
          break;
      }
    });

    return sortByNumber(resultArray, 'creationDate', -1);
  }

  @computed
  get subtotal() {
    return this.allGroups.reduce((finalSum, nextItem) => finalSum + nextItem.subtotal, 0);
  }

  @computed
  get totalQuantity() {
    return this.allGroups.reduce((finalSum, nextGroup) => finalSum + nextGroup.totalQuantity, 0);
  }

  @computed
  get productsNumber() {
    return this.allGroups.reduce((finalSumm, group) => {
      finalSumm += group.products.length;
      return finalSumm;
    }, 0);
  }

  @action.bound
  getRetailers() {
    const products = this.getProducts();
    const { channelSightSettings } = this.rootStore.optionsStore;
    const labelStore = this.rootStore.labelsStore;

    pubSub.publish(CART_TRANSFER_INIT, {
      products,
      optionsObject: channelSightSettings,
      labelsObject: labelStore,
    });

    pubSub.subscribe(
      CART_TRANSFER_READY,
      action((result) => {
        if (result) {
          this.hasRetailersFlag = hasOwnProperty(result, 'CartId');
          this.cartTransferResponseData = result;
        } else {
          this.hasRetailersFlag = false;
        }
      }),
    );
  }

  @computed
  get isCartTransferAvailable() {
    return (
      this.rootStore.optionsStore.isCartTransferAvailable &&
      this.getProducts().length > 0 &&
      this.hasRetailersFlag
    );
  }

  @computed
  get hasCards() {
    const curType = constants.groupType[this.rootStore.uiStore.type];

    switch (curType) {
      case constants.groupType.GROUP:
      case constants.groupType.SINGLE:
        return this.selectByGroupType.some((group) => group.products.length);
      case constants.groupType.ALL:
        return !!this.selectByGroupType.length;
      default:
        log.warn('[cartStore] Unexpected group type');
        return false;
    }
  }

  @computed
  get isEmptyCart() {
    return !this.allGroups.some((group) => group.products.length);
  }

  @action.bound
  removeGroup({ item, contentText = '', withoutConfirm = false }) {
    return this.rootStore.alterationsStore.whenRemoveItem({
      searchArr: this.allGroups,
      withoutConfirm,
      contentText:
        contentText ||
        this.rootStore.labelsStore.giftlistConfirmationQuestion.replace('#ID#', item.name),
      item,
    });
  }

  @action.bound
  forceRemoveAll() {
    this.allGroups = [];
  }

  @action.bound
  addGroup({ groupType = '', item }) {
    const shouldExpandGroup = !this.rootStore.uiStore.isMobile;
    const group = this.createGroup({ groupType, item, shouldExpandGroup });

    this.allGroups.push(group);
    this.rootStore.uiStore.showNotification(constants.ADD);
    return group;
  }

  createGroup({ groupType = '', item, shouldExpandGroup = false }) {
    const groupConfig = {
      item,
      rootStore: this.rootStore,
      shouldExpandGroup,
      remove: this.removeGroup,
    };
    let groupStore;

    switch (groupType) {
      case constants.substitute:
        groupStore = new GroupSubstitutions(groupConfig);
        break;
      default:
        groupStore = new GroupConfigurations(groupConfig);
        break;
    }

    return groupStore;
  }

  getProductCardById(productID, groupType = constants.groupType.SINGLE) {
    const groups = this.getGroupsByType(groupType);
    let productCard = null;

    if (groups.length) {
      groups.forEach((group) => {
        const card = group.products.find((product) => product.skuID === productID);

        if (card) {
          productCard = card;
        }
      });
    }

    return productCard;
  }

  getProductGroupCardById(configurationMasterId) {
    const groups = this.getGroupsByType(constants.groupType.GROUP);

    return groups.find((group) => group.configurationMasterId === configurationMasterId);
  }

  getGroupsByType(groupType) {
    return this.allGroups.filter((group) => group.type === groupType);
  }

  addProductToGroup(productObj, groupType = constants.groupType.SINGLE) {
    const [targetGroup] = this.getGroupsByType(groupType);

    if (targetGroup) {
      targetGroup.addCard(productObj);
    }
  }
}
